import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import FAQSection from '@/components/ui/FAQSection';
import PricingCard from '@/components/ui/PricingCard';
import { Search, BarChart3, FileText, Globe, TrendingUp, Headphones } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import ServiceCard from '@/components/ui/ServiceCard';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const SEOServices = () => {
  const seoServices = [
    {
      icon: <Search />,
      title: "Keyword Research",
      description: "Comprehensive keyword research to identify the most valuable search terms for your business and industry.",
      badge: "Research"
    },
    {
      icon: <BarChart3 />,
      title: "SEO Audit",
      description: "In-depth analysis of your website's current SEO performance, identifying issues and opportunities for improvement.",
      badge: "Analysis"
    },
    {
      icon: <FileText />,
      title: "On-Page Optimization",
      description: "Optimization of title tags, meta descriptions, headings, content, and other on-page elements to improve search rankings.",
      badge: "On-Page"
    },
    {
      icon: <Globe />,
      title: "Technical SEO",
      description: "Technical improvements to enhance website performance, crawlability, and indexation by search engines.",
      badge: "Technical"
    },
    {
      icon: <TrendingUp />,
      title: "Rank Tracking",
      description: "Ongoing monitoring of your keyword rankings and search visibility to measure progress and identify new opportunities.",
      badge: "Tracking"
    },
    {
      icon: <Headphones />,
      title: "SEO Consulting",
      description: "Expert guidance and recommendations to help you implement effective SEO strategies for your business.",
      badge: "Consulting"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to see results from SEO?",
      answer: "SEO is a long-term strategy that typically takes 3-6 months to start showing significant results. The timeline varies based on factors like your website's current state, competition level, and the scope of SEO work. We focus on building a strong foundation and implementing sustainable strategies that deliver lasting results rather than quick fixes that may not stand the test of time."
    },
    {
      question: "Do you guarantee first-page rankings?",
      answer: "We don't guarantee specific rankings as search engine algorithms are constantly evolving and no one can control Google's ranking decisions. What we do promise is to implement industry best practices, make data-driven decisions, and continuously optimize your SEO strategy to improve your search visibility and organic traffic over time. We provide transparent reporting so you can see the progress we're making."
    },
    {
      question: "What makes your SEO services different from others?",
      answer: "Our approach to SEO is comprehensive and tailored to your specific business goals. We don't use cookie-cutter strategies or focus solely on rankings. Instead, we develop customized SEO plans that consider your industry, target audience, and business objectives. We also emphasize education, ensuring you understand the strategies we're implementing and why they matter for your online success."
    },
    {
      question: "Do you focus on local SEO for businesses serving specific areas?",
      answer: "Absolutely! For businesses serving specific geographic areas, we implement local SEO strategies including Google Business Profile optimization, local keyword targeting, citation building, and review management. These tactics help you appear in local search results, map listings, and the 'near me' searches that potential customers in your area are performing."
    },
    {
      question: "How do you report on SEO performance?",
      answer: "We provide comprehensive monthly reports that track key metrics such as keyword rankings, organic traffic, conversion rates, and other relevant KPIs. These reports include analysis of the data and strategic recommendations. We also offer regular check-in calls to discuss performance, answer questions, and adjust the strategy as needed to ensure we're on track to meet your goals."
    }
  ];

  const pricingCards = [
    {
      title: "Pro Plan",
      price: "$600",
      setupFee: "+$400 setup fee",
      description: "Monthly SEO service for small businesses looking to improve their organic search presence.",
      features: [
        { text: "10 Keywords", included: true },
        { text: "No Backlinks", included: false },
        { text: "No Content", included: false },
        { text: "On-Page SEO", included: true },
        { text: "Monthly Progress Report", included: true }
      ],
      popular: false,
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Plus Plan",
      price: "$800",
      setupFee: "+$400 setup fee",
      description: "Comprehensive SEO service for businesses looking to significantly improve their search rankings.",
      features: [
        { text: "20 Keywords", included: true },
        { text: "DA 10+ Backlinks", included: true },
        { text: "10 Backlinks", included: true },
        { text: "13 In-Content Backlinks", included: true },
        { text: "1 Blog Article", included: true },
        { text: "500 Words", included: true },
        { text: "On-Page SEO", included: true },
        { text: "Monthly Progress Report", included: true }
      ],
      popular: true,
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Platinum Plan",
      price: "$1000",
      setupFee: "+$400 setup fee",
      description: "Advanced SEO solution for businesses with complex needs or highly competitive industries.",
      features: [
        { text: "40 Keywords", included: true },
        { text: "DA 30+ Backlinks", included: true },
        { text: "15 Backlinks", included: true },
        { text: "23 In-Content Backlinks", included: true },
        { text: "2 Blog Articles", included: true },
        { text: "1000 Words", included: true },
        { text: "On-Page SEO", included: true },
        { text: "Monthly Progress Report", included: true }
      ],
      popular: false,
      ctaText: "Get Started",
      ctaLink: "/contact"
    }
  ];

  return (
    <PageLayout 
      title="SEO Services | Astrem" 
      description="Comprehensive SEO services to improve your website's visibility in search engines and drive organic traffic to your business."
      ogImage="public/lovable-uploads/fb5b40d5-23c1-4bba-ac11-0be9116e9c28.png"
    >
      <PageHero 
        title="SEO Services" 
        subtitle="Boost your search rankings and organic traffic" 
        description="Our SEO services help businesses improve their visibility in search engines, drive more organic traffic, and increase conversions with sustainable, white-hat optimization strategies."
        badge="Search Engine Optimization"
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_using_laptop_website_services.jpg"
        ctaLink="/contact"
      />
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our SEO Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We provide comprehensive SEO services to help your business rank higher in search engines and drive more organic traffic to your website.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {seoServices.map((service, index) => (
              <ScrollReveal key={index} delay={200 + (index * 50)}>
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  badge={service.badge}
                  href="/contact"
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Near-Me Website Technology Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Local SEO Innovation
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Near-Me Website Technology
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Our innovative Near-Me website technology enables fast local website deployments for businesses targeting multiple cities or locations.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Target multiple cities like Baton Rouge, Lafayette, Lake Charles, Covington, Hammond, New Orleans and LaPlace
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Perfect for franchises with multiple locations or service-based businesses
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Edit content on the master website to update all deployed sites at once
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Make location-specific edits that only affect individual sites
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Each location site receives its own form submissions assigned to specific admin users
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Integrated with our CRM, marketing tech, funnel hosting, and automation tools
                    </span>
                  </li>
                </ul>
                
                <div className="bg-white p-6 rounded-lg border border-astrem-gray-200 mb-6">
                  <h3 className="text-lg font-medium mb-3">URL Structure Example:</h3>
                  <div className="space-y-2 mb-2 font-mono text-sm text-astrem-gray-700">
                    <p>buildwithlm.com <span className="text-astrem-gray-500">(Master Site Homepage)</span></p>
                    <p>buildwithlm.com/la/lafayette <span className="text-astrem-gray-500">(Lafayette Site Homepage)</span></p>
                    <p>buildwithlm.com/about <span className="text-astrem-gray-500">(Master Site About Page)</span></p>
                    <p>buildwithlm.com/la/lafayette/about/ <span className="text-astrem-gray-500">(Lafayette Site About Page)</span></p>
                  </div>
                </div>
                
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Learn More About Near-Me Technology</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1517292987719-0369a794ec0f?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Near-Me Website Technology" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section id="monthly-seo-services" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2426&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Monthly SEO services" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Ongoing Optimization
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Monthly SEO Services
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Our ongoing SEO services help your business maintain and improve search rankings with continuous optimization and strategy adjustments.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Targeted keyword optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Regular content updates and optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Technical SEO improvements
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Performance reporting and strategy adjustments
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Get Started with Monthly SEO</Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section id="on-page-optimization" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  On-Page SEO
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  On-Page Optimization
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  We optimize your website's content and structure to improve search visibility and ensure it meets all of Google's best practices.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Title and meta description optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Heading tag structure improvement
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Image alt tag optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Content optimization for target keywords
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Improve Your On-Page SEO</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1555421689-3f034debb7a6?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="On-page SEO optimization" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our SEO Process
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We follow a proven process to ensure your website achieves and maintains high rankings in search engines.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">1</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Audit & Research</h3>
                <p className="text-astrem-gray-600">
                  We analyze your current SEO performance, identify opportunities, and research your industry and competitors.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">2</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Strategy Development</h3>
                <p className="text-astrem-gray-600">
                  We create a customized SEO strategy tailored to your business goals, target audience, and competitive landscape.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">3</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Implementation</h3>
                <p className="text-astrem-gray-600">
                  We implement on-page, technical, and content optimizations to improve your search visibility and user experience.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={500}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center h-full">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">4</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Monitoring & Refinement</h3>
                <p className="text-astrem-gray-600">
                  We continuously monitor performance, make data-driven adjustments, and refine the strategy to improve results.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Ready to Improve Your Search Rankings?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how our SEO services can help your business achieve higher rankings, more traffic, and better results from search engines.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
              <Link to="/contact">Start Your SEO Journey</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              SEO Service Packages
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Choose from our SEO packages or contact us for a custom solution tailored to your needs.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pricingCards.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <FAQSection faqs={faqs} />
    </PageLayout>
  );
};

export default SEOServices;
